/**
 * Epic 12 Phase A: Operational state model for consistent degraded/unknown UX.
 * Merges uiHealthStatus with optional page-level signals.
 */

import { uiHealthStatus } from './uiHealth';

export type OpsState = 'healthy' | 'degraded' | 'down' | 'unknown';

export interface OpsStateInputs {
  /** Optional page-level signals (e.g. backlog counts, drift summary). */
  pageSignals?: {
    degraded?: boolean;
    down?: boolean;
    reasons?: Array<{ title: string; detail?: string; severity?: 'info' | 'warning' | 'error' }>;
  };
}

export interface OpsStateResult {
  state: OpsState;
  reasons: Array<{ title: string; detail?: string; severity?: 'info' | 'warning' | 'error' }>;
  detail?: Record<string, unknown>;
}

/**
 * Derives a single OpsState from uiHealthStatus and optional page signals.
 * uiHealth 'ok' maps to 'healthy'; page signals can worsen but not improve.
 */
export function deriveOpsState(inputs: OpsStateInputs = {}): OpsStateResult {
  const ui = uiHealthStatus();
  const page = inputs.pageSignals;

  const reasons: Array<{ title: string; detail?: string; severity?: 'info' | 'warning' | 'error' }> = [];

  // Map uiHealth status to OpsState
  let state: OpsState = ui.status === 'ok' ? 'healthy' : ui.status === 'degraded' ? 'degraded' : ui.status === 'down' ? 'down' : 'unknown';

  if (ui.status === 'unknown') {
    reasons.push({
      title: 'Signals missing',
      detail: 'No recent API responses yet. This is normal on fresh load.',
      severity: 'info',
    });
  } else if (ui.status === 'degraded') {
    const failureClass = String(ui.detail?.primary_failure_class || ui.detail?.last_error?.failure_class || '');
    const title = failureClass === 'query_timeout'
      ? 'Cloud API query timeout'
      : failureClass === 'backend_degraded'
        ? 'Cloud API backend degraded'
        : failureClass === 'network'
          ? 'Cloud API connectivity unstable'
          : 'Cloud API connectivity unstable';
    reasons.push({
      title,
      detail: ui.detail?.last_error?.message ? String(ui.detail.last_error.message).slice(0, 200) : undefined,
      severity: 'warning',
    });
  } else if (ui.status === 'down') {
    const failureClass = String(ui.detail?.primary_failure_class || ui.detail?.last_error?.failure_class || '');
    reasons.push({
      title: failureClass === 'backend_degraded' || failureClass === 'query_timeout'
        ? 'Cloud API backend unavailable'
        : 'Cloud API unreachable',
      detail: ui.detail?.last_error?.message ? String(ui.detail.last_error.message).slice(0, 200) : undefined,
      severity: 'error',
    });
  }

  // Page-level signals can worsen state
  if (page?.reasons?.length) {
    reasons.push(...page.reasons);
    if (page.down && state !== 'down') state = 'down';
    else if (page.degraded && state === 'healthy') state = 'degraded';
  }

  return {
    state,
    reasons,
    detail: ui.detail,
  };
}

export function opsStateLabel(state: OpsState): string {
  switch (state) {
    case 'healthy':
      return 'Healthy';
    case 'degraded':
      return 'Degraded';
    case 'down':
      return 'Down';
    case 'unknown':
      return 'Unknown (signals missing)';
    default:
      return String(state);
  }
}

export function opsStateColor(state: OpsState): 'success' | 'warning' | 'error' | 'default' {
  switch (state) {
    case 'healthy':
      return 'success';
    case 'degraded':
      return 'warning';
    case 'down':
      return 'error';
    case 'unknown':
      return 'default';
    default:
      return 'default';
  }
}

export function opsStateRank(state: OpsState): number {
  switch (state) {
    case 'down':
      return 4;
    case 'degraded':
      return 3;
    case 'unknown':
      return 2;
    case 'healthy':
      return 1;
    default:
      return 0;
  }
}

/**
 * Minimal context snippet for Phase A "Copy context" button.
 * Phase B3 will expand with full contextBundle.ts.
 */
export function buildMinimalContextSnippet(params: {
  orgId?: string;
  siteId?: string;
  page?: string;
  state: OpsState;
  reasons?: Array<{ title: string; detail?: string }>;
  url?: string;
}): string {
  const lines: string[] = [
    `[Edgentik Ops Context]`,
    `state: ${params.state}`,
    `org: ${params.orgId ?? '—'}`,
    `site: ${params.siteId ?? '—'}`,
    `page: ${params.page ?? '—'}`,
    `ts: ${new Date().toISOString()}`,
    `url: ${params.url ?? (typeof window !== 'undefined' ? window.location.href : '—')}`,
  ];
  if (params.reasons?.length) {
    lines.push('');
    lines.push('reasons:');
    params.reasons.forEach((r) => lines.push(`  - ${r.title}${r.detail ? `: ${r.detail}` : ''}`));
  }
  return lines.join('\n');
}
